// client/src/hooks/useFidelidade.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';
import useAlert from './useAlert';

const errMsg = (err, fallback) => err.response?.data?.error || err.response?.data?.message || fallback;

// ---------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------

export const useFidelidadeConfig = () => useQuery({
    queryKey: ['fidelidade', 'config'],
    queryFn: () => apiClient.get('/fidelidade/config').then((r) => r.data.data),
});

export const useSalvarFidelidadeConfig = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();
    return useMutation({
        mutationFn: (payload) => apiClient.put('/fidelidade/config', payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['fidelidade', 'config'] });
            setAlert('Configuração de fidelidade salva com sucesso.', 'success');
        },
        onError: (err) => setAlert(errMsg(err, 'Erro ao salvar configuração de fidelidade.'), 'danger'),
    });
};

// ---------------------------------------------------------------------
// Saldo e extrato de um cliente
// ---------------------------------------------------------------------

export const useSaldoFidelidade = (clienteId) => useQuery({
    queryKey: ['fidelidade', 'saldo', clienteId],
    queryFn: () => apiClient.get(`/fidelidade/clientes/${clienteId}/saldo`).then((r) => r.data.data.saldo),
    enabled: !!clienteId,
});

export const useExtratoFidelidade = (clienteId, params = {}) => useQuery({
    queryKey: ['fidelidade', 'extrato', clienteId, params],
    queryFn: () => apiClient.get(`/fidelidade/clientes/${clienteId}/extrato`, { params }).then((r) => r.data),
    enabled: !!clienteId,
    placeholderData: (prev) => prev,
});

export const useResgatarDesconto = (clienteId) => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();
    return useMutation({
        mutationFn: (payload) => apiClient.post(`/fidelidade/clientes/${clienteId}/resgatar-desconto`, payload),
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ['fidelidade', 'saldo', clienteId] });
            queryClient.invalidateQueries({ queryKey: ['fidelidade', 'extrato', clienteId] });
            const { valorDesconto } = res.data.data;
            setAlert(`Resgate feito — aplique R$ ${Number(valorDesconto).toFixed(2)} de desconto na venda.`, 'success');
        },
        onError: (err) => setAlert(errMsg(err, 'Erro ao resgatar pontos.'), 'danger'),
    });
};

export const useResgatarPremio = (clienteId) => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();
    return useMutation({
        mutationFn: (premioId) => apiClient.post(`/fidelidade/clientes/${clienteId}/resgatar-premio`, { premioId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['fidelidade', 'saldo', clienteId] });
            queryClient.invalidateQueries({ queryKey: ['fidelidade', 'extrato', clienteId] });
            queryClient.invalidateQueries({ queryKey: ['fidelidade', 'premios'] });
            queryClient.invalidateQueries({ queryKey: ['fidelidade', 'resgates'] });
            setAlert('Prêmio resgatado! Acompanhe a entrega na fila de resgates.', 'success');
        },
        onError: (err) => setAlert(errMsg(err, 'Erro ao resgatar prêmio.'), 'danger'),
    });
};

// ---------------------------------------------------------------------
// Catálogo de prêmios
// ---------------------------------------------------------------------

export const usePremiosFidelidade = (params = {}) => useQuery({
    queryKey: ['fidelidade', 'premios', params],
    queryFn: () => apiClient.get('/fidelidade/premios', { params }).then((r) => r.data.data),
});

export const useMutatePremiosFidelidade = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();
    const invalidate = () => queryClient.invalidateQueries({ queryKey: ['fidelidade', 'premios'] });

    const criarPremio = useMutation({
        mutationFn: (payload) => apiClient.post('/fidelidade/premios', payload),
        onSuccess: () => { invalidate(); setAlert('Prêmio criado com sucesso.', 'success'); },
        onError: (err) => setAlert(errMsg(err, 'Erro ao criar prêmio.'), 'danger'),
    });

    const atualizarPremio = useMutation({
        mutationFn: ({ id, ...payload }) => apiClient.put(`/fidelidade/premios/${id}`, payload),
        onSuccess: () => { invalidate(); setAlert('Prêmio atualizado com sucesso.', 'success'); },
        onError: (err) => setAlert(errMsg(err, 'Erro ao atualizar prêmio.'), 'danger'),
    });

    const excluirPremio = useMutation({
        mutationFn: (id) => apiClient.delete(`/fidelidade/premios/${id}`),
        onSuccess: () => { invalidate(); setAlert('Prêmio removido com sucesso.', 'success'); },
        onError: (err) => setAlert(errMsg(err, 'Erro ao remover prêmio.'), 'danger'),
    });

    return { criarPremio, atualizarPremio, excluirPremio };
};

// ---------------------------------------------------------------------
// Fila de resgates
// ---------------------------------------------------------------------

export const useResgatesFidelidade = (params = {}) => useQuery({
    queryKey: ['fidelidade', 'resgates', params],
    queryFn: () => apiClient.get('/fidelidade/resgates', { params }).then((r) => r.data),
    placeholderData: (prev) => prev,
});

export const useMutateResgatesFidelidade = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();
    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ['fidelidade', 'resgates'] });
        queryClient.invalidateQueries({ queryKey: ['fidelidade', 'premios'] });
    };

    const confirmarEntrega = useMutation({
        mutationFn: (id) => apiClient.post(`/fidelidade/resgates/${id}/entregar`),
        onSuccess: () => { invalidate(); setAlert('Entrega confirmada.', 'success'); },
        onError: (err) => setAlert(errMsg(err, 'Erro ao confirmar entrega.'), 'danger'),
    });

    const cancelarResgate = useMutation({
        mutationFn: ({ id, motivo }) => apiClient.post(`/fidelidade/resgates/${id}/cancelar`, { motivo }),
        onSuccess: () => {
            invalidate();
            queryClient.invalidateQueries({ queryKey: ['fidelidade', 'saldo'] });
            queryClient.invalidateQueries({ queryKey: ['fidelidade', 'extrato'] });
            setAlert('Resgate cancelado — pontos estornados ao cliente.', 'success');
        },
        onError: (err) => setAlert(errMsg(err, 'Erro ao cancelar resgate.'), 'danger'),
    });

    return { confirmarEntrega, cancelarResgate };
};
